import React, { useState, useEffect, useContext } from 'react';
import { Card, Row, Col, Nav, Form, Button, Alert, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaCalendarAlt, FaFileExport, FaPrint, FaEnvelope, FaInfoCircle, FaChartBar, FaTable, FaFilter } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import AuthContext from '../../context/AuthContext';

// Import report components
import ProjectsReport from './report-types/ProjectsReport';
import FinancialReport from './report-types/FinancialReport';
import LaborReport from './report-types/LaborReport';
import ClientsReport from './report-types/ClientsReport';

// Import styles
import './ReportsDashboard.css';

const ReportsDashboard = () => {
  const { token } = useContext(AuthContext);
  
  // State for report selection and parameters
  const [activeCategory, setActiveCategory] = useState('projects');
  const [reportType, setReportType] = useState('');
  const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 3)));
  const [endDate, setEndDate] = useState(new Date());
  const [filters, setFilters] = useState({
    clientId: '',
    projectId: '',
    employeeId: '',
    status: ''
  });
  
  // State for display options
  const [viewMode, setViewMode] = useState('chart'); // 'chart', 'table', 'both'
  const [filtersVisible, setFiltersVisible] = useState(true);
  
  // State for available filter options
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    projects: [],
    employees: [],
    statuses: []
  });
  
  // State for report data and UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  
  // State for multi-select clients (for Projects by Client report)
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  
  // Report categories and types
  const reportCategories = [
    {
      id: 'projects',
      name: 'Project Reports',
      icon: 'project-icon',
      description: 'View and analyze project status, timeline, and details',
      reports: [
        { id: 'projects-by-status', name: 'Projects by Status', description: 'Overview of projects grouped by their current status' },
        { id: 'projects-by-date', name: 'Projects by Date', description: 'Projects created or active during a specific time period' },
        { id: 'projects-by-client', name: 'Projects by Client', description: 'View all projects for selected clients' }
      ]
    },
    {
      id: 'financial',
      name: 'Financial Reports',
      icon: 'financial-icon',
      description: 'Track revenue, profitability, and payment information',
      reports: [
        { id: 'revenue-summary', name: 'Revenue Summary', description: 'Summary of revenue across projects and time periods' },
        { id: 'project-profitability', name: 'Project Profitability', description: 'Revenue minus Labor and Materials costs' },
        { id: 'outstanding-payments', name: 'Outstanding Payments', description: 'List of invoices due or overdue from clients' }
      ]
    },
    {
      id: 'labor',
      name: 'Labor Reports',
      icon: 'labor-icon',
      description: 'Monitor employee hours, costs, and productivity',
      reports: [
        { id: 'labor-hours', name: 'Labor Hours', description: 'Summary of labor hours by employee and project' },
        { id: 'labor-costs', name: 'Labor Costs', description: 'Analysis of labor costs across projects' },
        { id: 'overtime-analysis', name: 'Overtime Analysis', description: 'Review of overtime hours and costs' }
      ]
    },
    {
      id: 'clients',
      name: 'Client Reports',
      icon: 'client-icon',
      description: 'Assess client relationships, revenue, and project history',
      reports: [
        { id: 'client-activity', name: 'Client Activity', description: 'Overview of client engagement and activity' },
        { id: 'client-projects', name: 'Client Projects', description: 'Summary of projects by client' },
        { id: 'client-revenue', name: 'Client Revenue', description: 'Analysis of revenue contribution by client' }
      ]
    }
  ];
  
  // Fetch filter options when component mounts
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await fetch('/api/reports/filters', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch filter options');
        }
        
        const data = await response.json();
        setFilterOptions(data);
      } catch (err) {
        console.error('Error fetching filter options:', err);
        setError('Failed to load filter options. Please try again.');
      }
    };
    
    fetchFilterOptions();
  }, [token]);
  
  // Handle report selection
  const handleReportSelect = (reportId) => {
    setReportType(reportId);
    setReportData(null);
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      clientId: '',
      projectId: '',
      employeeId: '',
      status: ''
    });
    setSelectedClientIds([]);
    setStartDate(new Date(new Date().setMonth(new Date().getMonth() - 3)));
    setEndDate(new Date());
  };
  
  // Generate report
  const generateReport = async () => {
    if (!reportType) {
      setError('Please select a report type');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Only add date parameters if this report uses date range
      const currentFilters = getAvailableFilters();
      if (!currentFilters.includes('noDateRange')) {
        params.append('startDate', format(startDate, 'yyyy-MM-dd'));
        params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      }
      
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.projectId) params.append('projectId', filters.projectId);
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.status) params.append('status', filters.status);
      
      // Get the category from the report type
      const category = reportType.split('-')[0];
      
      // Build API endpoint based on report type
      let endpoint;
      if (reportType === 'projects-by-status') {
        endpoint = `/api/reports/projects/by-status`;
      } else if (reportType === 'projects-by-date') {
        endpoint = `/api/reports/projects/by-date`;
      } else if (reportType === 'projects-by-client') {
        endpoint = `/api/reports/clients/by-client`;
        // For multi-select clients, use selectedClientIds if available, else use clientId
        if (selectedClientIds.length > 0) {
          params.append('clientIds', selectedClientIds.join(','));
        } else if (filters.clientId) {
          params.append('clientIds', filters.clientId);
        }
      } else if (reportType === 'revenue-summary') {
        endpoint = `/api/reports/financial/revenue-summary`;
      } else if (reportType === 'project-profitability') {
        endpoint = `/api/reports/financial/project-profitability`;
      } else if (reportType === 'outstanding-payments') {
        endpoint = `/api/reports/financial/outstanding-payments`;
      } else if (reportType === 'labor-hours') {
        endpoint = `/api/reports/labor/hours`;
      } else if (reportType === 'labor-costs') {
        endpoint = `/api/reports/labor/costs`;
      } else if (reportType === 'overtime-analysis') {
        endpoint = `/api/reports/labor/overtime`;
      } else if (reportType === 'client-activity') {
        endpoint = `/api/reports/clients/activity`;
      } else if (reportType === 'client-projects') {
        endpoint = `/api/reports/clients/projects`;
      } else if (reportType === 'client-revenue') {
        endpoint = `/api/reports/clients/revenue`;
      }
      
      // Fetch report data
      const response = await fetch(`${endpoint}?${params.toString()}`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const data = await response.json();
      
      // Set report data
      setReportData({
        type: reportType,
        data: data,
        params: {
          startDate,
          endDate,
          filters: { ...filters }
        }
      });
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message || 'Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Export report as PDF
  const exportReport = () => {
    window.print();
  };

  // Email report
  const emailReport = () => {
    // Implementation for emailing the report would go here
    alert('Email functionality will be implemented soon.');
  };

  // Export as Excel
  const exportAsExcel = () => {
    // Implementation for exporting as Excel would go here
    alert('Export as Excel functionality will be implemented soon.');
  };
  
  // Get available filters for selected report
  const getAvailableFilters = () => {
    const selectedReport = reportCategories
      .flatMap(category => category.reports)
      .find(report => report.id === reportType);
    
    if (!selectedReport) return [];
    
    // Mapping report type to available filters
    switch (reportType) {
      case 'projects-by-status':
      case 'projects-by-date':
        return ['client', 'status'];
      case 'projects-by-client':
        return ['multiClient'];
      case 'revenue-summary':
      case 'project-profitability':
        return ['client', 'project'];
      case 'outstanding-payments':
        return ['client', 'project', 'noDateRange'];
      case 'labor-hours':
      case 'labor-costs':
      case 'overtime-analysis':
        return ['employee', 'project'];
      case 'client-activity':
      case 'client-projects':
      case 'client-revenue':
        return ['client'];
      default:
        return [];
    }
  };
  
  // Get report component based on type
  const getReportComponent = () => {
    if (!reportData) return null;
    
    // Determine which report component to render based on the category
    if (reportType.startsWith('projects-') && reportType !== 'projects-by-client') {
      return <ProjectsReport data={reportData} viewMode={viewMode} />;
    } else if (reportType === 'projects-by-client') {
      return <ClientsReport data={reportData} viewMode={viewMode} />;
    } else if (reportType.startsWith('revenue-') || reportType === 'project-profitability' || reportType === 'outstanding-payments') {
      return <FinancialReport data={reportData} viewMode={viewMode} />;
    } else if (reportType.startsWith('labor-') || reportType === 'overtime-analysis') {
      return <LaborReport data={reportData} viewMode={viewMode} />;
    } else if (reportType.startsWith('client-')) {
      return <ClientsReport data={reportData} viewMode={viewMode} />;
    }
    
    return null;
  };
  
  // Available filters for the selected report
  const availableFilters = getAvailableFilters();
  
  return (
    <div className="reports-dashboard color-scheme-3">
      <div className="dashboard-header">
        <h1 className="page-title">Reports Dashboard</h1>
        <div className="view-controls">
          <div className="btn-group" role="group">
            <Button 
              variant={viewMode === 'chart' ? 'primary' : 'outline-primary'} 
              onClick={() => setViewMode('chart')}
              className="control-btn"
            >
              <FaChartBar /> Charts
            </Button>
            <Button 
              variant={viewMode === 'table' ? 'primary' : 'outline-primary'} 
              onClick={() => setViewMode('table')}
              className="control-btn"
            >
              <FaTable /> Tables
            </Button>
            <Button 
              variant={viewMode === 'both' ? 'primary' : 'outline-primary'} 
              onClick={() => setViewMode('both')}
              className="control-btn"
            >
              <FaChartBar /> <FaTable /> Both
            </Button>
          </div>
          <Button 
            variant={filtersVisible ? 'primary' : 'outline-primary'} 
            onClick={() => setFiltersVisible(!filtersVisible)}
            className="filter-toggle"
          >
            <FaFilter /> {filtersVisible ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>
      </div>
      
      <div className="report-selection-container">
        <Row>
          <Col md={3}>
            <Card className="category-selector">
              <Card.Body>
                <h5 className="mb-3">Report Categories</h5>
                <Nav className="flex-column" variant="pills">
                  {reportCategories.map(category => (
                    <Nav.Item key={category.id}>
                      <Nav.Link 
                        active={activeCategory === category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className="d-flex align-items-center"
                      >
                        <div className={`category-icon ${category.icon}`} />
                        <span>{category.name}</span>
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
                {activeCategory && (
                  <div className="category-description mt-3">
                    <p className="text-muted">
                      {reportCategories.find(cat => cat.id === activeCategory)?.description}
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={9}>
            <Card className="report-options">
              <Card.Body>
                <h5 className="mb-3">Select a Report</h5>
                
                <div className="report-type-list">
                  {reportCategories.find(cat => cat.id === activeCategory)?.reports.map(report => (
                    <div 
                      key={report.id}
                      className={`report-type-card ${reportType === report.id ? 'selected' : ''}`}
                      onClick={() => handleReportSelect(report.id)}
                    >
                      <h6>{report.name}</h6>
                      <p>{report.description}</p>
                    </div>
                  ))}
                </div>
                
                {reportType && filtersVisible && (
                  <div className="report-params mt-4">
                    <h5 className="mb-3">Report Parameters</h5>
                    
                    {!availableFilters.includes('noDateRange') && (
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="d-flex align-items-center">
                              <FaCalendarAlt className="me-2" /> Date Range
                            </Form.Label>
                            <div className="d-flex">
                              <div className="me-2 date-picker-container">
                                <DatePicker
                                  selected={startDate}
                                  onChange={date => setStartDate(date)}
                                  selectsStart
                                  startDate={startDate}
                                  endDate={endDate}
                                  className="form-control"
                                  dateFormat="MMM d, yyyy"
                                />
                              </div>
                              <div className="date-picker-container">
                                <DatePicker
                                  selected={endDate}
                                  onChange={date => setEndDate(date)}
                                  selectsEnd
                                  startDate={startDate}
                                  endDate={endDate}
                                  minDate={startDate}
                                  className="form-control"
                                  dateFormat="MMM d, yyyy"
                                />
                              </div>
                            </div>
                          </Form.Group>
                        </Col>
                        
                        <Col md={6}>
                          <div className="preset-ranges">
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={() => {
                                setStartDate(new Date(new Date().setDate(new Date().getDate() - 30)));
                                setEndDate(new Date());
                              }}
                              className="me-2"
                            >
                              Last 30 Days
                            </Button>
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={() => {
                                setStartDate(new Date(new Date().setDate(new Date().getDate() - 90)));
                                setEndDate(new Date());
                              }}
                              className="me-2"
                            >
                              Last 90 Days
                            </Button>
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={() => {
                                const now = new Date();
                                setStartDate(new Date(now.getFullYear(), now.getMonth(), 1));
                                setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
                              }}
                            >
                              This Month
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    )}
                    
                    <Row className="mt-3">
                      {availableFilters.includes('client') && (
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="d-flex align-items-center">
                              Client
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Filter report by client</Tooltip>}
                              >
                                <span><FaInfoCircle className="ms-2 text-muted" /></span>
                              </OverlayTrigger>
                            </Form.Label>
                            <Form.Select
                              name="clientId"
                              value={filters.clientId}
                              onChange={handleFilterChange}
                            >
                              <option value="">All Clients</option>
                              {filterOptions.clients.map(client => (
                                <option key={client.ClientId} value={client.ClientId}>
                                  {client.Name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}
                      
                      {availableFilters.includes('project') && (
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="d-flex align-items-center">
                              Project
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Filter report by project</Tooltip>}
                              >
                                <span><FaInfoCircle className="ms-2 text-muted" /></span>
                              </OverlayTrigger>
                            </Form.Label>
                            <Form.Select
                              name="projectId"
                              value={filters.projectId}
                              onChange={handleFilterChange}
                            >
                              <option value="">All Projects</option>
                              {filterOptions.projects.map(project => (
                                <option key={project.ProjectId} value={project.ProjectId}>
                                  {project.ProjectName}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}
                      
                      {availableFilters.includes('employee') && (
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="d-flex align-items-center">
                              Employee
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Filter report by employee</Tooltip>}
                              >
                                <span><FaInfoCircle className="ms-2 text-muted" /></span>
                              </OverlayTrigger>
                            </Form.Label>
                            <Form.Select
                              name="employeeId"
                              value={filters.employeeId}
                              onChange={handleFilterChange}
                            >
                              <option value="">All Employees</option>
                              {filterOptions.employees.map(employee => (
                                <option key={employee.EmployeeId} value={employee.EmployeeId}>
                                  {employee.FirstName} {employee.LastName}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}
                      
                      {availableFilters.includes('status') && (
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="d-flex align-items-center">
                              Status
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Filter report by status</Tooltip>}
                              >
                                <span><FaInfoCircle className="ms-2 text-muted" /></span>
                              </OverlayTrigger>
                            </Form.Label>
                            <Form.Select
                              name="status"
                              value={filters.status}
                              onChange={handleFilterChange}
                            >
                              <option value="">All Statuses</option>
                              {filterOptions.statuses.map(status => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}
                      
                      {availableFilters.includes('multiClient') && (
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="d-flex align-items-center">
                              Select Clients
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Select one or more clients to view their projects</Tooltip>}
                              >
                                <span><FaInfoCircle className="ms-2 text-muted" /></span>
                              </OverlayTrigger>
                            </Form.Label>
                            <div className="multi-client-select" style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '4px', padding: '10px' }}>
                              {filterOptions.clients.map(client => (
                                <Form.Check
                                  key={client.ClientId}
                                  type="checkbox"
                                  id={`client-${client.ClientId}`}
                                  label={client.Name}
                                  checked={selectedClientIds.includes(client.ClientId.toString())}
                                  onChange={(e) => {
                                    const clientId = client.ClientId.toString();
                                    if (e.target.checked) {
                                      setSelectedClientIds([...selectedClientIds, clientId]);
                                    } else {
                                      setSelectedClientIds(selectedClientIds.filter(id => id !== clientId));
                                    }
                                  }}
                                  className="mb-1"
                                />
                              ))}
                            </div>
                            {selectedClientIds.length > 0 && (
                              <small className="text-muted">
                                {selectedClientIds.length} client(s) selected
                              </small>
                            )}
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                    
                    <div className="d-flex justify-content-between mt-3">
                      <Button 
                        variant="outline-secondary" 
                        onClick={resetFilters}
                      >
                        Reset Filters
                      </Button>
                      <Button 
                        variant="primary" 
                        onClick={generateReport}
                        disabled={isLoading || !reportType}
                      >
                        {isLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Generating...
                          </>
                        ) : 'Generate Report'}
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      
      {error && (
        <Alert variant="danger" className="mt-4">
          {error}
        </Alert>
      )}
      
      {reportData && (
        <div className="report-results mt-4" id="report-container">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">
                  {reportCategories
                    .flatMap(category => category.reports)
                    .find(report => report.id === reportType)?.name || 'Report Results'}
                </h4>
                <p className="text-muted mb-0 mt-1">
                  {!availableFilters.includes('noDateRange') && (
                    <>{format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}</>
                  )}
                  {filters.clientId && filterOptions.clients.length > 0 && 
                    `${!availableFilters.includes('noDateRange') ? ' | ' : ''}Client: ${filterOptions.clients.find(c => c.ClientId.toString() === filters.clientId)?.Name || ''}`}
                  {filters.projectId && filterOptions.projects.length > 0 && 
                    ` | Project: ${filterOptions.projects.find(p => p.ProjectId.toString() === filters.projectId)?.ProjectName || ''}`}
                  {filters.status && ` | Status: ${filters.status}`}
                  {availableFilters.includes('noDateRange') && !filters.clientId && !filters.projectId && 'All outstanding payments'}
                </p>
              </div>
              <div className="report-actions">
                <Button 
                  variant="outline-secondary" 
                  className="me-2" 
                  onClick={exportReport}
                >
                  <FaPrint className="me-1" /> Print
                </Button>
                <Button 
                  variant="outline-primary" 
                  className="me-2"
                  onClick={exportAsExcel}
                >
                  <FaFileExport className="me-1" /> Export
                </Button>
                <Button 
                  variant="outline-info"
                  onClick={emailReport}
                >
                  <FaEnvelope className="me-1" /> Email
                </Button>
              </div>
            </Card.Header>
            
            <Card.Body>
              {getReportComponent()}
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReportsDashboard;